import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ExternalLink, FileText, Loader2, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge } from "./ui";

const PLAN_LABEL = { facility_monthly: "Facility · $1,290/mo", enterprise_monthly: "Enterprise GxP · $4,900/mo" };

export default function Billing() {
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    api.get("/billing/summary")
      .then(({ data }) => setData(data))
      .catch(() => setData({ active: false, plan: null, invoices: [] }));
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setOpening(false); }
  };

  if (!data) return <div className="p-8 font-mono text-xs text-muted-foreground">LOADING BILLING…</div>;

  return (
    <div>
      <PageHeader eyebrow="ACCOUNT" title="Billing" subtitle="Manage your subscription, payment method and invoices">
        {data.active && (
          <Button data-testid="manage-billing-btn" onClick={openPortal} disabled={opening} className="rounded-none bg-primary hover:bg-primary/90 text-white">
            {opening ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Opening…</> : <><CreditCard className="h-4 w-4 mr-1" /> Manage Billing</>}
          </Button>
        )}
      </PageHeader>

      <div className="p-8">
        {!data.active && !data.plan ? (
          <div className="card-elevated rounded-sm p-10 text-center max-w-lg mx-auto" data-testid="billing-empty">
            <div className="h-14 w-14 rounded-sm bg-white/5 border border-border flex items-center justify-center mx-auto">
              <CreditCard className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-xl font-bold tracking-tight mt-5">No active subscription</h2>
            <p className="text-sm text-muted-foreground mt-2">This account isn't linked to a Vaultvec plan yet.</p>
            <Link to="/#pricing"><Button className="rounded-sm bg-primary hover:bg-primary/90 text-white mt-6 glow-primary">View plans <ArrowUpRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="card-elevated p-6">
                <span className="text-xs text-muted-foreground uppercase tracking-widest">Plan</span>
                <div className="font-heading text-lg font-bold mt-2">{PLAN_LABEL[data.plan] || data.plan || "—"}</div>
              </div>
              <div className="card-elevated p-6">
                <span className="text-xs text-muted-foreground uppercase tracking-widest">Status</span>
                <div className="mt-3"><StatusBadge value={data.active ? "active" : (data.status || "inactive")} /></div>
                {data.cancel_at_period_end && <p className="font-mono text-[10px] text-primary mt-2 tracking-widest">CANCELS AT PERIOD END</p>}
              </div>
              <div className="card-elevated p-6">
                <span className="text-xs text-muted-foreground uppercase tracking-widest">Renews</span>
                <div className="font-mono text-sm mt-3">{data.current_period_end ? new Date(data.current_period_end * 1000).toLocaleDateString() : "—"}</div>
              </div>
            </div>

            <div className="card-elevated rounded-sm overflow-hidden">
              <div className="border-b border-border px-4 py-3 font-mono text-[10px] tracking-widest text-muted-foreground flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> INVOICES</div>
              {data.invoices.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No invoices yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="invoices-table">
                  <tbody>
                    {data.invoices.map((inv, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3 font-mono text-xs">{inv.number || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{inv.currency?.toUpperCase()} {inv.amount?.toFixed(2)}</td>
                        <td className="px-4 py-3"><StatusBadge value={inv.status === "paid" ? "verified" : "pending"} /></td>
                        <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{inv.date ? new Date(inv.date * 1000).toLocaleDateString() : ""}</td>
                        <td className="px-4 py-3 text-right">
                          {inv.url && <a href={inv.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">View <ExternalLink className="h-3 w-3" /></a>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
